/**
 * URL segment from service title (Strapi `slug` field removed — paths are `/usluge/<slugified-title>`).
 * ASCII, hyphenated; Serbian Latin + Cyrillic → Latin; fallback `usluga-<id>` if needed.
 */

const SR_LATIN_REPLACEMENTS: [string, string][] = [
  ['đ', 'dj'],
  ['Đ', 'Dj'],
  ['ž', 'z'],
  ['Ž', 'Z'],
  ['ć', 'c'],
  ['Ć', 'C'],
  ['č', 'c'],
  ['Č', 'C'],
  ['š', 's'],
  ['Š', 'S'],
]

/** Multi-char Cyrillic first (Serbian), then single-char map handles the rest. */
const CYR_MULTIS: [RegExp, string][] = [
  [/љ/g, 'lj'],
  [/Љ/g, 'Lj'],
  [/њ/g, 'nj'],
  [/Њ/g, 'Nj'],
  [/џ/g, 'dz'],
  [/Џ/g, 'Dz'],
  [/ђ/g, 'dj'],
  [/Ђ/g, 'Dj'],
  [/ж/g, 'zh'],
  [/Ж/g, 'Zh'],
  [/ч/g, 'ch'],
  [/Ч/g, 'Ch'],
  [/ш/g, 'sh'],
  [/Ш/g, 'Sh'],
]

const CYR_SINGLE: Record<string, string> = {
  а: 'a',
  б: 'b',
  в: 'v',
  г: 'g',
  д: 'd',
  е: 'e',
  з: 'z',
  и: 'i',
  ј: 'j',
  к: 'k',
  л: 'l',
  м: 'm',
  н: 'n',
  о: 'o',
  п: 'p',
  р: 'r',
  с: 's',
  т: 't',
  ћ: 'c',
  у: 'u',
  ф: 'f',
  х: 'h',
  ц: 'c',
  А: 'a',
  Б: 'b',
  В: 'v',
  Г: 'g',
  Д: 'd',
  Е: 'e',
  З: 'z',
  И: 'i',
  Ј: 'j',
  К: 'k',
  Л: 'l',
  М: 'm',
  Н: 'n',
  О: 'o',
  П: 'p',
  Р: 'r',
  С: 's',
  Т: 't',
  Ћ: 'c',
  У: 'u',
  Ф: 'f',
  Х: 'h',
  Ц: 'c',
}

/** Same fold as slugify, but keeps words as spaces — for client-side title search (diacritics / Cyrillic / ASCII). */
export function normalizeTitleForSearch(input: string): string {
  let s = input.trim()
  if (!s) return ''

  for (const [a, b] of SR_LATIN_REPLACEMENTS) {
    s = s.split(a).join(b)
  }
  for (const [re, rep] of CYR_MULTIS) {
    s = s.replace(re, rep)
  }
  s = s
    .split('')
    .map((ch) => CYR_SINGLE[ch] ?? ch)
    .join('')

  s = s.normalize('NFKD').replace(/\p{M}/gu, '')

  return s
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
}

/**
 * True if every word in `rawQuery` appears in `title` after the same normalization
 * (e.g. "svadba" matches "Svadbа", "š" vs "s", "đ" vs "dj", Cyrillic titles vs Latin typing).
 */
export function titleMatchesSearchQuery(title: string, rawQuery: string): boolean {
  const q = normalizeTitleForSearch(rawQuery)
  if (!q) return true
  const t = normalizeTitleForSearch(title)
  const tokens = q.split(' ').filter(Boolean)
  return tokens.every((tok) => t.includes(tok))
}

export function slugifyServiceTitle(title: string): string {
  const s = normalizeTitleForSearch(title)
  if (!s) return ''
  return s.replace(/\s+/g, '-')
}

export function serviceUrlSegment(service: { title: string; id: number }): string {
  const slug = slugifyServiceTitle(service.title)
  return slug || `usluga-${service.id}`
}
